import {Theme} from "@material-ui/core";
import {ThemeOptions} from "@material-ui/core";
import {AlertOptions, PluginConfiguration} from "../types";
import {LanguageDefinition} from "../types";
import {LocaleDefinition} from "../types";
import uid from "./uid";
import {ISOCK_ACTION_EVENT_RECEIVED} from "../iframe-sock/iframeSocket";
import IframeSocket, {
    ISOCK_ACTION_CONNECT,
    IsockMessage,
    ISOCK_ACTION_MESSAGE_RECEIVED,
    ISOCK_ACTION_READY
} from '../iframe-sock/iframeSocket';
import {TAuditLogParameters, TAuditLogQueryColumns, TAuditLogFlags, User} from "../types";
import {OpsPortalEventHandler} from "../types";
import {OpsPortalEventType} from "../types";
import {OpsPortalEvent} from "../types";
import {Callback, CountryDefinition, IOpsSdkAsync, PluginDefinition, TCancelTokenSource} from "../types";
import Bus from '../bus/bus';
import {
    METHOD_ADD_EVENT_LISTENER,
    METHOD_ALERT_REMOVE,
    METHOD_ALERT_SET,
    METHOD_CALL,
    METHOD_EXTEND_MUI_THEME,
    METHOD_GET_ACCESS_TOKEN,
    METHOD_GET_REFRESHED_ACCESS_TOKEN,
    METHOD_GET_BRAND_NAME,
    METHOD_GET_CANCEL_TOKEN_SOURCE,
    METHOD_GET_COUNTRIES,
    METHOD_GET_COUNTRY,
    METHOD_GET_ENTITY_ID,
    METHOD_GET_GOOGLE_TOKEN,
    METHOD_GET_I18N_RESOURCE,
    METHOD_GET_LANGUAGE,
    METHOD_GET_LOCALES_BY_COUNTRY,
    METHOD_GET_MUI_THEME,
    METHOD_GET_OKTA_TOKEN,
    METHOD_GET_PLUGINS,
    METHOD_GET_PLUGIN,
    METHOD_GET_PORTAL_URL,
    METHOD_GET_THEME_OPTIONS,
    METHOD_GET_USER,
    METHOD_GET_USER_NAME,
    METHOD_GET_USER_ID,
    METHOD_PLUGIN_URL_GET,
    METHOD_PLUGIN_URL_SET,
    METHOD_PUSH_GTM_DATA_LAYER,
    METHOD_PUSH_GTM_EVENT,
    METHOD_REFRESH_ACCESS_TOKEN,
    METHOD_REFRESH_GOOGLE_TOKEN,
    METHOD_REMOVE_EVENT_LISTENER,
    METHOD_SET_PLUGIN_LOADED,
    METHOD_SWITCH_COUNTRY,
    METHOD_SWITCH_PLUGIN,
    METHOD_TRANSLATE,
    METHOD_AUDIT_LOG_OPEN,
    METHOD_GET_ENVIRONMENT,
    METHOD_GET_REGION,
    METHOD_GET_CONFIGURATION,
    METHOD_LOG_ERROR_OBJECT,
    METHOD_GET_FEATURE,
    METHOD_GET_MARKET_NAME,
} from "./methods";

const TIMEOUT_LIMIT = 15000;

export default class Client {
    topWindow: Window;
    sock: IframeSocket;
    channel: MessageChannel;
    port: MessagePort;
    portalUrl: string;
    promiseQueue: { [k: string]: { resolve: Function, reject: Function } };
    bus: Bus<OpsPortalEvent> = new Bus<OpsPortalEvent>();

    constructor(portalUrl: string, win: Window = window.top, channel: MessageChannel = new MessageChannel()) {
        this.topWindow = win;
        this.portalUrl = portalUrl;
        this.promiseQueue = {};
        this.channel = channel;
        this.port = this.channel.port1;
        this.sock = new IframeSocket(this.port, 'CLIENT');
    }

    init(cb?: Callback<IOpsSdkAsync>): Promise<IOpsSdkAsync> {
        this.sock.on(ISOCK_ACTION_MESSAGE_RECEIVED, this.onMessageReturn.bind(this));
        this.sock.on(ISOCK_ACTION_EVENT_RECEIVED, this.onEventReceived.bind(this));
        const p = new Promise<IOpsSdkAsync>(
            (resolve, reject) => {
                //once we get the READY back from the Provider resolve the promise with the IOpsSdkAsync sdk object
                this.sock.on(ISOCK_ACTION_READY, () => resolve(this.getSdk() as IOpsSdkAsync));
                //initiate a connection with the provider, and transfer the other port of the message channel to the Provider in the main window
                this.topWindow.postMessage(JSON.stringify({action: ISOCK_ACTION_CONNECT}), this.portalUrl, [this.channel.port2]);
                //reject promise if not resolved before timing out
                setTimeout(() => reject("CLIENT TIMED OUT WHILE ESTABLISHING CONNECTION"), TIMEOUT_LIMIT);
            });

        if (cb) {
            p.then((sdk) => cb(null, sdk)).catch(e => cb(e));
        } else {
            return p;
        }
    }

    onMessageReturn(msg: IsockMessage) {
        if (this.promiseQueue.hasOwnProperty(msg.id)) {
            this.promiseQueue[msg.id].resolve(msg.payload);
            delete this.promiseQueue[msg.id];
        }
    }

    onEventReceived(msg: IsockMessage) {
        const ev = msg.payload.event as OpsPortalEvent;
        this.bus.trigger(ev.type, ev);
    }

    send<T = any>(action: string, payload: any = null, cb?: Callback): Promise<T> {
        const id = uid();
        const message = {id, action, payload, created: new Date()};
        const p = new Promise<T>((resolveFn, rejectFn) => {
            this.promiseQueue[id] = {
                resolve: resolveFn,
                reject: rejectFn
            };

            this.port.postMessage(JSON.stringify(message));
            setTimeout(() => rejectFn(new Error('CLIENT TIMED OUT FOR MSG ID: ' + id)), TIMEOUT_LIMIT);
        });

        if (cb) {
            p.then((result) => cb(null, result)).catch(e => cb(e));
        } else {
            return p;
        }
    }

    getSdk(): IOpsSdkAsync {
        return {
            setPluginLoaded: (cb?: Callback<null>) => this.send(METHOD_SET_PLUGIN_LOADED,null, cb),
            getUserName: (cb?: Callback<string>) => this.send<string>(METHOD_GET_USER_NAME,null, cb),
            getUser: (cb?: Callback<User|null>) => this.send(METHOD_GET_USER, null, cb),
            getUserId: (cb?: Callback<string>) => this.send(METHOD_GET_USER_ID, null, cb),
            getPlugins: (cb?: Callback<PluginDefinition[]>) => this.send<PluginDefinition[]>(METHOD_GET_PLUGINS, null, cb),
            getPlugin: (cb?: Callback<PluginDefinition>) => this.send<PluginDefinition>(METHOD_GET_PLUGIN, null, cb),
            getCountries: (cb?: Callback<CountryDefinition[]>) => this.send<CountryDefinition[]>(METHOD_GET_COUNTRIES, null, cb),
            getAccessToken: (cb?: Callback<string>) => {
                const isError = (result: string): boolean => result.indexOf("Error:") === 0;
                const removePrefix = (result: string): string => result.replace("Error: ", "");

                const p = this.send<string>(METHOD_GET_ACCESS_TOKEN);
                if (cb) {
                    p.then((result) => {
                        if (isError(result)) {
                            cb(new Error(removePrefix(result)))
                        } else {
                            cb(null, result)
                        }
                    }).catch(e => cb(e));
                } else {
                    return p.then(result => {
                        if (isError(result)) {
                            return Promise.reject(removePrefix(result));
                        }
                        return result;
                    })
                }
            },
            getRefreshedAccessToken: (cb?: Callback<string>) => this.send<string>(METHOD_GET_REFRESHED_ACCESS_TOKEN, null, cb),
            refreshAccessToken: (cb?: Callback<void>) => this.send<void>(METHOD_REFRESH_ACCESS_TOKEN, null, cb),
            getMuiTheme: (cb?: Callback<object>) => this.send<object>(METHOD_GET_MUI_THEME, null, cb),
            getCountry: (cb?: Callback<CountryDefinition>) => this.send<CountryDefinition>(METHOD_GET_COUNTRY, null, cb),
            getLanguage: (cb?: Callback<LanguageDefinition>) => this.send<LanguageDefinition>(METHOD_GET_LANGUAGE, null, cb),
            getLocalesByCountry: (country: CountryDefinition, cb?: Callback<Array<LocaleDefinition>>) => this.send(METHOD_GET_LOCALES_BY_COUNTRY, country, cb),
            getEntityId: (cb?: Callback<string>) => this.send<string>(METHOD_GET_ENTITY_ID, null, cb),
            getBrandName: (cb?: Callback<string>) => this.send<string>(METHOD_GET_BRAND_NAME, null, cb),
            getMarketName: (cb?: Callback<string>) => this.send<string>(METHOD_GET_MARKET_NAME, null, cb),
            translate: (key: string, cb: Callback<string>) => this.send<string>(METHOD_TRANSLATE, { key }, cb),
            call: (opt: {url: string, data: any, method?: string, headers?: object, axiosOptions?: object}, cb?: Callback) => {
                const message = {
                    url: opt.url,
                    data: opt.data,
                    headers: opt.headers || {},
                    method: opt.method || "GET",
                    axiosOptions: opt.axiosOptions || {}
                };
                return this.send(METHOD_CALL, message, cb);
            },
            getCancelTokenSource: (cb?: Callback<TCancelTokenSource>) => this.send(METHOD_GET_CANCEL_TOKEN_SOURCE, null, cb),
            portal: {
                getUrl: (cb?: Callback<string>) => this.send<string>(METHOD_GET_PORTAL_URL,null, cb),
                switchPlugin: (id: string, cb?: Callback<boolean>) => this.send<boolean>(METHOD_SWITCH_PLUGIN, {pluginId: id}, cb),
                switchCountry: (countryCode: string, cb?: Callback<boolean>) => this.send<boolean>(METHOD_SWITCH_COUNTRY, {countryCode}, cb),
                pluginUrl: {
                    set: (url: string|null, cb?: Callback<boolean>) => this.send<boolean>(METHOD_PLUGIN_URL_SET, {url}, cb),
                    get: (cb?: Callback<string>) => this.send<string>(METHOD_PLUGIN_URL_GET, null, cb),
                }
            },
            addEventListener: (eventType: OpsPortalEventType, listener: OpsPortalEventHandler, cb?: Callback<boolean>) => {

                // If we have more listeners for this event, no need to notify the provider to start sending this event
                if (listener && this.bus.listenerCount(eventType) > 0) {
                    this.bus.on(eventType, listener);
                    if (cb) {
                        cb(null, true);
                        return;
                    }
                    return Promise.resolve(true);
                }

                // if we need to use a callback instead of a promise
                const confirmationCb = (err: Error|null|undefined, isEventListenerSet: boolean|any) => {
                    if (!err && isEventListenerSet) {
                        this.bus.on(eventType, listener);
                        cb(null, isEventListenerSet);
                    } else {
                        const errMsg = err ? err.message : "";
                        cb(new Error("Unable to set event listener on Provider " + errMsg), isEventListenerSet);
                    }
                };

                const confirmationPromise = this.send(METHOD_ADD_EVENT_LISTENER, {eventType}, cb ? confirmationCb : undefined);
                if (confirmationPromise) {
                    return confirmationPromise.then(
                        isEventListenerSet => {
                            this.bus.on(eventType, listener);
                            return isEventListenerSet;
                        }
                    );
                }
            },
            removeEventListener(eventType: OpsPortalEventType, listener?: OpsPortalEventHandler, cb?:  Callback<boolean>){

                // If we have more listeners for this event (and are not clearing them all),
                // no need to notify the provider to stop sending this event
                if (listener && this.bus.listenerCount(eventType) > 1) {
                    this.bus.off(eventType, listener);
                    if (cb) {
                        cb(null, true);
                        return;
                    }
                    return Promise.resolve(true);
                }

                // if we need to use a callback instead of a promise
                const confirmationCb = (isEventListenerRemoved: boolean|any) => {
                    if (isEventListenerRemoved) {
                        this.bus.off(eventType, listener);
                        cb(null, isEventListenerRemoved);
                    } else {
                        cb(new Error("Unable to remove event listener on Provider"), isEventListenerRemoved);
                    }
                };

                const confirmationPromise = this.send(METHOD_REMOVE_EVENT_LISTENER, {eventType, removeAll: listener === undefined}, cb ? confirmationCb : undefined);
                if (confirmationPromise) {
                    return confirmationPromise.then(
                        isEventListenerRemoved => {
                            this.bus.off(eventType, listener);
                            return isEventListenerRemoved;
                        }
                    );
                }
            },
            getGoogleToken: (cb?: Callback<gapi.auth2.AuthResponse | null>) => this.send<gapi.auth2.AuthResponse | null>(METHOD_GET_GOOGLE_TOKEN, null, cb),
            getOktaToken: (cb?: Callback<string>) => this.send<string>(METHOD_GET_OKTA_TOKEN, null, cb),
            refreshGoogleToken: (cb?: Callback<void>) => this.send<void>(METHOD_REFRESH_GOOGLE_TOKEN, null, cb),
            getI18nResource: (cb?: Callback<object>) => this.send<object>(METHOD_GET_I18N_RESOURCE, null, cb),
            getThemeOptions: (cb?: Callback<ThemeOptions>) => this.send<ThemeOptions>(METHOD_GET_THEME_OPTIONS, null, cb),
            extendMuiTheme: (options: ThemeOptions = {}, cb?: Callback<Theme>) => this.send<Theme>(METHOD_EXTEND_MUI_THEME, {options}, cb),
            alert: {
                set: (msg: string, options: AlertOptions = {}, cb?: Callback<string>) => this.send<string>(METHOD_ALERT_SET, {msg, options}, cb),
                remove: (key:string, cb?: Callback<void>) => this.send<void>(METHOD_ALERT_REMOVE, {key}, cb)
            },
            pushGtmDataLayer: (dataLayer: Object, layerName?: string) => this.send<void>(METHOD_PUSH_GTM_DATA_LAYER, {dataLayer, layerName}),
            pushGtmEvent: (name: string, data?: string|number|object) => this.send<void>(METHOD_PUSH_GTM_EVENT, {name, data}),
            auditLog: {
                open: (schemaName: string, columns: TAuditLogQueryColumns, parameters: TAuditLogParameters, flags: TAuditLogFlags) => this.send<void>(METHOD_AUDIT_LOG_OPEN, {schemaName, columns, parameters, flags}),
            },
            getEnvironment: (cb?: Callback<string>) => this.send<string>(METHOD_GET_ENVIRONMENT, null, cb),
            getRegion: (cb?: Callback<string>) => this.send<string>(METHOD_GET_REGION, null, cb),
            getConfiguration: (cb?: Callback<PluginConfiguration>) => this.send<PluginConfiguration>(METHOD_GET_CONFIGURATION, null, cb),
            logErrorObject: (error: Error, cb?: Callback<void>) => this.send<void>(METHOD_LOG_ERROR_OBJECT, {error}, cb),
            getFeature: (featureIdentifier: string, cb?: Callback<boolean>) => this.send<boolean>(METHOD_GET_FEATURE, {featureId: featureIdentifier}, cb),
        }
    }
}
